import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { UploadCloud, X, Star, Loader2, Images } from "lucide-react";
import { toast } from "sonner";
import { MediaLibrary } from "./admin/MediaLibrary";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Store absolute URLs so images render across the app. Relative /api/files paths get the backend origin.
const toAbsolute = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export const ImageUploader = ({ images = [], onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const urls = (data.urls || []).map(toAbsolute);
      onChange([...images, ...urls]);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const makePrimary = (i) => {
    const next = [...images];
    const [img] = next.splice(i, 1);
    onChange([img, ...next]);
  };

  return (
    <div data-testid="image-uploader">
      <div className="flex flex-wrap gap-3 mb-3">
        {images.filter(Boolean).map((img, i) => (
          <div key={i} className="relative w-20 h-24 rounded-lg overflow-hidden border group" data-testid={`uploaded-image-${i}`}>
            <img src={img} alt="" className="w-full h-full object-cover" />
            {i === 0 && (
              <span className="absolute top-0 left-0 bg-[#E63946] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-br">PRIMARY</span>
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
              {i !== 0 && (
                <button type="button" onClick={() => makePrimary(i)} title="Make primary"
                  className="w-7 h-7 rounded-full bg-white flex items-center justify-center" data-testid={`make-primary-${i}`}>
                  <Star size={13} className="text-[#D4AF37]" />
                </button>
              )}
              <button type="button" onClick={() => remove(i)} title="Remove"
                className="w-7 h-7 rounded-full bg-white flex items-center justify-center" data-testid={`remove-image-${i}`}>
                <X size={14} className="text-red-500" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple hidden onChange={handleFiles} data-testid="image-upload-input" />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="image-upload-btn">
          {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UploadCloud size={16} className="mr-2" />}
          {uploading ? "Uploading..." : "Upload photos"}
        </Button>
        <Button type="button" variant="outline" onClick={() => setLibraryOpen(true)} data-testid="image-library-btn">
          <Images size={16} className="mr-2" /> Choose from library
        </Button>
      </div>
      <MediaLibrary open={libraryOpen} onOpenChange={setLibraryOpen}
        onSelect={(urls) => onChange([...images.filter(Boolean), ...urls.filter((u) => !images.includes(u))])} />
      <p className="text-xs text-slate-400 mt-1.5">JPG, PNG, WebP up to 8MB each. First image is the primary. Hover an image to set primary or remove.</p>
    </div>
  );
};
